import type { LandscapingConfig } from '@paint-ai/shared';
import { Check, Sparkles } from 'lucide-react';
import { cn } from '@/lib/cn';
import { AVAILABLE_PLANTS, CUSTOM_LANDSCAPING, LAWN_STYLES, PATHWAY_STYLES } from '../../data/landscaping';

interface LandscapingFineTuneProps {
  config: LandscapingConfig;
  onChange: (config: LandscapingConfig) => void;
}

const select =
  'w-full text-xs bg-white border border-slate-200 rounded-lg p-2 font-medium text-slate-800 focus:outline-none focus:ring-2 focus:ring-emerald-500';
const fieldLabel = 'text-[11px] font-bold text-slate-600 block mb-1';

/** Plant, lawn and pathway overrides. Any change turns the selection into a custom preset. */
export function LandscapingFineTune({ config, onChange }: LandscapingFineTuneProps) {
  const customize = (changes: Partial<LandscapingConfig>) => onChange({ ...config, ...CUSTOM_LANDSCAPING, ...changes });

  const togglePlant = (plant: string) => {
    const plants = config.plants ?? [];
    customize({ plants: plants.includes(plant) ? plants.filter((p) => p !== plant) : [...plants, plant] });
  };

  return (
    <div className="bg-slate-50 border border-slate-200/80 rounded-xl p-4 space-y-4 pt-3">
      <div className="flex items-center gap-1.5 text-xs font-bold text-slate-800">
        <Sparkles className="w-3.5 h-3.5 text-amber-500" />
        Fine-Tune Landscaping Elements
      </div>

      <fieldset>
        <legend className={cn(fieldLabel, 'mb-1.5')}>Select Native Indian Flora & Plants:</legend>
        <div className="flex flex-wrap gap-1.5">
          {AVAILABLE_PLANTS.map((plant) => {
            const checked = config.plants?.includes(plant) ?? false;
            return (
              <button
                key={plant}
                type="button"
                aria-pressed={checked}
                onClick={() => togglePlant(plant)}
                className={cn(
                  'text-[11px] font-medium px-2.5 py-1 rounded-lg border transition-all flex items-center gap-1',
                  checked
                    ? 'bg-emerald-600 text-white border-emerald-600 shadow-xs'
                    : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300',
                )}
              >
                {checked && <Check className="w-3 h-3 stroke-[3]" />}
                {plant}
              </button>
            );
          })}
        </div>
      </fieldset>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label>
          <span className={fieldLabel}>Lawn & Turf Style:</span>
          <select
            value={config.lawnStyle}
            onChange={(e) => customize({ lawnStyle: e.target.value })}
            className={select}
          >
            {LAWN_STYLES.map((style) => (
              <option key={style} value={style}>
                {style}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span className={fieldLabel}>Walkway & Pathway Style:</span>
          <select
            value={config.pathwayStyle}
            onChange={(e) => customize({ pathwayStyle: e.target.value })}
            className={select}
          >
            {PATHWAY_STYLES.map((style) => (
              <option key={style} value={style}>
                {style}
              </option>
            ))}
          </select>
        </label>
      </div>

      <label className="block">
        <span className={fieldLabel}>Custom Landscaping Instructions (Optional):</span>
        <input
          type="text"
          placeholder="e.g. Add red terracotta planters on either side of the entrance step..."
          value={config.customNotes || ''}
          onChange={(e) => onChange({ ...config, customNotes: e.target.value })}
          className="w-full text-xs bg-white border border-slate-200 rounded-lg p-2 text-slate-800 focus:outline-none focus:ring-2 focus:ring-emerald-500 placeholder:text-slate-400"
        />
      </label>
    </div>
  );
}
